import { useTranslation } from 'react-i18next';
import { Phone, MessageSquare, User, Zap, Clock, AlertTriangle } from 'lucide-react';
import { formatResponseTime } from '@/hooks/useTeamMembers';
import type { TeamMember } from '@/types/database';

interface HostContactCardProps {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  avatarUrl: string | null;
  onChatClick: () => void;
  darkMode: boolean;
  // New props for real status
  isOnline?: boolean;
  lastSeenAt?: string | null;
  chatEnabled?: boolean;
  responseTimeMinutes?: number | null;
  // Emergency contact (shown when host is offline)
  emergencyContact?: TeamMember | null;
}

// Format last seen time
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatLastSeen(lastSeenAt: string | null, t: any): string {
  if (!lastSeenAt) return '';

  const lastSeen = new Date(lastSeenAt);
  const now = new Date();
  const diffMs = now.getTime() - lastSeen.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return t('guest.host.justNow', 'Upravo');
  if (diffMinutes < 60) return t('guest.host.lastSeenMinutes', '{{minutes}} min').replace('{{minutes}}', String(diffMinutes));

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t('guest.host.lastSeenHours', '{{hours}}h').replace('{{hours}}', String(diffHours));

  const diffDays = Math.floor(diffHours / 24);
  return t('guest.host.lastSeenDays', '{{days}}d').replace('{{days}}', String(diffDays));
}

export function HostContactCard({
  firstName,
  lastName,
  phone,
  avatarUrl,
  onChatClick,
  darkMode,
  isOnline = false,
  lastSeenAt,
  chatEnabled = true,
  responseTimeMinutes,
  emergencyContact,
}: HostContactCardProps) {
  const { t } = useTranslation();

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || t('guest.host.yourHost', 'Vaš domaćin');
  const formattedResponseTime = formatResponseTime(responseTimeMinutes);
  const formattedLastSeen = formatLastSeen(lastSeenAt ?? null, t);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-cyan-50/30 border border-cyan-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />

      <div className="relative p-5">
        {/* Host Info */}
        <div className="flex items-center gap-4 mb-5">
          {/* Avatar */}
          <div className="relative">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={fullName}
                className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white/20"
              />
            ) : (
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                darkMode
                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600'
                  : 'bg-gradient-to-br from-cyan-400 to-blue-500'
              } ring-4 ring-white/20 shadow-lg`}>
                <User className="w-8 h-8 text-white" />
              </div>
            )}
            {/* Online Indicator - Now dynamic */}
            <div
              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                darkMode ? 'border-slate-800' : 'border-white'
              } ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`}
            >
              {isOnline && (
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </div>
          </div>

          {/* Name & Status */}
          <div className="flex-1">
            <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {fullName}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('guest.host.yourHost', 'Vaš domaćin')}
            </p>

            {/* Online/Offline Status with Response Time */}
            <div className="flex items-center gap-2 mt-1">
              {isOnline ? (
                <span className={`text-xs font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {t('guest.host.online', 'Online')}
                </span>
              ) : (
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  {formattedLastSeen ? (
                    <>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {t('guest.host.lastSeen', 'Zadnji put')}: {formattedLastSeen}
                    </>
                  ) : (
                    t('guest.host.offline', 'Offline')
                  )}
                </span>
              )}

              {/* Response Time */}
              {formattedResponseTime && (
                <>
                  <span className={`${darkMode ? 'text-slate-600' : 'text-gray-300'}`}>•</span>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Zap className="w-3 h-3 inline mr-0.5 text-yellow-500" />
                    {formattedResponseTime}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Chat paused: the guest can still open the chat and read the host's
            messages (the host can still write), just not send — say so, and
            point at the phone. */}
        {!chatEnabled && (
          <div className={`mb-4 p-3 rounded-xl ${
            darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
          }`}>
            <p className="text-sm font-medium">
              {t('guest.chat.paused.title', 'Domaćin je pauzirao chat')}
            </p>
            {phone && (
              <p className="text-xs mt-0.5 opacity-90">
                {t('guest.host.chatPausedCall', 'Za nešto hitno nazovite domaćina.')}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          {/* Phone Button */}
          {phone && (
            <a
              href={`tel:${phone}`}
              className={`flex items-center justify-center gap-2 p-3.5 rounded-xl transition-all duration-200 ${
                darkMode
                  ? 'bg-slate-700/50 hover:bg-slate-700 text-white'
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
              }`}
            >
              <Phone className="w-5 h-5" />
              <span className="text-sm font-medium">{t('guest.host.call', 'Nazovi')}</span>
            </a>
          )}

          {/* Chat Button — opens the chat even when paused (read-only then) */}
          <button
            type="button"
            onClick={onChatClick}
            className={`flex items-center justify-center gap-2 p-3.5 rounded-xl transition-all duration-200 ${
              phone ? '' : 'col-span-2'
            } ${
              !chatEnabled
                ? darkMode
                  ? 'bg-slate-700/50 hover:bg-slate-700 text-white'
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                : darkMode
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white'
            } ${chatEnabled ? 'shadow-lg shadow-cyan-500/30' : ''}`}
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-sm font-medium">
              {chatEnabled ? t('guest.host.message', 'Pošalji poruku') : t('guest.host.openChat', 'Otvori chat')}
            </span>
          </button>
        </div>

        {/* Emergency Contact - Shown when host is offline */}
        {!isOnline && emergencyContact && (
          <div className={`mt-4 p-3 rounded-xl ${
            darkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50 border border-red-100'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
              <span className={`text-xs font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                {t('guest.host.emergencyContact', 'Za hitne slučajeve')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {emergencyContact.photo_url ? (
                  <img
                    src={emergencyContact.photo_url}
                    alt={emergencyContact.name}
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    darkMode ? 'bg-red-800/50' : 'bg-red-100'
                  }`}>
                    <User className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
                  </div>
                )}
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {emergencyContact.name}
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {t(`team.roles.${emergencyContact.role}`, emergencyContact.role)}
                  </p>
                </div>
              </div>
              {emergencyContact.phone && (
                <a
                  href={`tel:${emergencyContact.phone}`}
                  className={`p-2 rounded-lg ${
                    darkMode
                      ? 'bg-red-800/50 hover:bg-red-800 text-red-400'
                      : 'bg-red-100 hover:bg-red-200 text-red-600'
                  }`}
                >
                  <Phone className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Response Time Footer */}
        {responseTimeMinutes !== undefined && responseTimeMinutes !== null && responseTimeMinutes > 0 && (
          <p className={`mt-4 text-xs text-center ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            ⚡ {t('guest.host.respondsWithin', 'Obično odgovara unutar {{time}}').replace('{{time}}', formattedResponseTime || '')}
          </p>
        )}
      </div>
    </div>
  );
}

export default HostContactCard;
